import {
  Button,
  ButtonIcon,
  DuplicateIcon,
  FormField,
  Inline,
  TextArea,
  TextInput,
  Tooltip,
} from 'canoe';
import { Field, FieldAttributes, Form, Formik } from 'formik';
import {
  ActionFetch,
  ENDPOINTS,
  fetchResource,
  handleErrorResponse,
  useCreatePayload,
} from 'hammock';
import React, { useEffect, useState } from 'react';
import { useMutation } from 'react-query';
import * as Yup from 'yup';

import { ContentMetadata } from '@candu-ai/content/types';

export const validationSchema = Yup.object().shape({
  name: Yup.string()
    .min(3, 'Must have at least 3 characters')
    .max(255, 'Cannot be longer then 255 characters')
    .required('You must specify a name'),
  slug: Yup.string().min(3, 'Must have at least 3 characters').required('You must specify a slug'),
  description: Yup.string(),
});

interface InformationProps {
  contentMetadata: ContentMetadata;
  onContentUpdated?: VoidFunction;
  onContentUpdateError?: (error: unknown, variables: ActionFetch) => void;
}

type FormData = {
  name: string;
  description?: string;
  slug: string;
};

export const Information = ({
  contentMetadata,
  onContentUpdated,
  onContentUpdateError,
}: InformationProps) => {
  const { update } = useCreatePayload(ENDPOINTS.contentMetadata);
  const [hasCopied, setHasCopied] = useState<string | undefined>(undefined);
  const { mutateAsync: updateContent } = useMutation(
    (updatePayload: FormData) => {
      const payload = update({
        id: contentMetadata.id,
        body: { ...contentMetadata, ...updatePayload },
      });
      return fetchResource(payload).catch((e) => handleErrorResponse(e, payload));
    },
    {
      onSuccess: onContentUpdated,
      onError: (e, variables) =>
        onContentUpdateError?.(e, update({ id: contentMetadata.id, body: variables })),
    },
  );

  const onSubmit = async (payload: FormData) => {
    await updateContent(payload);
  };

  const onCopyField = (fieldName: keyof ContentMetadata) => {
    if (typeof contentMetadata[fieldName] === 'string') {
      navigator.clipboard.writeText(contentMetadata[fieldName] as string);
      setHasCopied(fieldName);
    }
  };

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    if (hasCopied) {
      timeoutId = setTimeout(() => {
        setHasCopied(undefined);
      }, 2000);
    }

    return () => clearTimeout(timeoutId);
  }, [hasCopied]);

  return (
    <Formik
      onSubmit={onSubmit}
      initialValues={{
        name: contentMetadata.name,
        description: contentMetadata.description,
        slug: contentMetadata.slug,
      }}
      validationSchema={validationSchema}
    >
      {({ isValid, isSubmitting, handleSubmit, dirty }) => (
        <Form onSubmit={handleSubmit}>
          <Field name="name">
            {({ field, meta }: FieldAttributes<any>) => (
              <>
                <FormField
                  label="Name"
                  necessity="required"
                  state={meta.touched && meta.error ? 'danger' : 'none'}
                  feedback={meta.touched ? meta.error : ''}
                  mt="s"
                >
                  <TextInput {...field} value={field.value} placeholder="Enter your content name" />
                </FormField>
              </>
            )}
          </Field>
          <Field name="slug">
            {({ field, meta }: FieldAttributes<any>) => (
              <>
                <FormField
                  label="Slug"
                  state={meta.touched && meta.error ? 'danger' : 'none'}
                  feedback={meta.touched ? meta.error : ''}
                  mt="s"
                >
                  <>
                    <Tooltip
                      content="Slug Copied!"
                      state="success"
                      trigger="manual"
                      placement="bottom"
                      visible={hasCopied === 'slug'}
                      usePortal={false}
                    >
                      <TextInput
                        {...field}
                        disabled
                        grow
                        value={contentMetadata.slug}
                        suffix={
                          <ButtonIcon
                            mr="xs"
                            icon={DuplicateIcon}
                            size="small"
                            color="contrast"
                            onClick={() => onCopyField('slug')}
                          />
                        }
                        placeholder="Slug"
                      />
                    </Tooltip>
                  </>
                </FormField>
              </>
            )}
          </Field>
          <Field name="id">
            {({ field, meta }: FieldAttributes<any>) => (
              <>
                <FormField
                  label="Content ID"
                  state={meta.touched && meta.error ? 'danger' : 'none'}
                  feedback={meta.touched ? meta.error : ''}
                  mt="s"
                >
                  <>
                    <Tooltip
                      content="Content ID Copied!"
                      state="success"
                      trigger="manual"
                      placement="bottom"
                      visible={hasCopied === 'id'}
                      usePortal={false}
                    >
                      <TextInput
                        {...field}
                        disabled
                        grow
                        value={contentMetadata.id}
                        suffix={
                          <ButtonIcon
                            mr="xs"
                            icon={DuplicateIcon}
                            size="small"
                            color="contrast"
                            onClick={() => onCopyField('id')}
                          />
                        }
                        placeholder="Content ID"
                      />
                    </Tooltip>
                  </>
                </FormField>
              </>
            )}
          </Field>
          <Field name="description" disabled>
            {({ field, meta }: FieldAttributes<any>) => (
              <>
                <FormField
                  label="Description"
                  state={meta.touched && meta.error ? 'danger' : 'none'}
                  feedback={meta.touched ? meta.error : ''}
                  mt="s"
                >
                  <TextArea {...field} value={field.value} placeholder="Enter description here" />
                </FormField>
              </>
            )}
          </Field>
          <Inline justify="flex-end" mt="l">
            <Button
              disabled={!isValid || !dirty}
              variant="secondary"
              loading={isSubmitting}
              type="submit"
            >
              Save changes
            </Button>
          </Inline>
        </Form>
      )}
    </Formik>
  );
};
